import { useState, useEffect } from "react";
import { usePostHog } from "posthog-js/react";
import { useAuth } from "@/features/auth";
import { telegramService } from "@/features/onboarding";
import { userService } from "@/features/settings/services/userService";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import intervalsIcuLogo from "@/intervalsicu-logo-192.png";

export function OnboardingPage() {
  const { user, updateUser, logout } = useAuth();
  const posthog = usePostHog();
  const isIntervalsConnected = user?.has_intervalsicu ?? false;
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  useEffect(() => {
    posthog.capture("onboarding:page_view");
  }, [posthog]);

  // Intervals.icu form state
  const [athleteId, setAthleteId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [isIntervalsLoading, setIsIntervalsLoading] = useState(false);
  const [intervalsError, setIntervalsError] = useState<string | null>(null);
  const [intervalsSuccess, setIntervalsSuccess] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

  // Telegram state
  const [isTelegramLoading, setIsTelegramLoading] = useState(false);
  const [telegramError, setTelegramError] = useState<string | null>(null);

  const handleLogout = () => {
    setShowLogoutDialog(false);
    logout();
    posthog.capture("onboarding:logout_complete");
  };

  const handleLogoutButtonClick = () => {
    posthog.capture("onboarding:logout_button_click");
    setShowLogoutDialog(true);
  };

  const handleSaveIntervals = async () => {
    if (!athleteId.trim() || !apiKey.trim()) {
      setIntervalsError("Both fields are required");
      return;
    }

    posthog.capture("onboarding:intervals_icu_connect_button_click");
    setIsIntervalsLoading(true);
    setIntervalsError(null);
    setIntervalsSuccess(false);

    try {
      await userService.setIntervalsICU({
        athlete_id: athleteId.trim(),
        api_key: apiKey.trim(),
      });
      updateUser({ has_intervalsicu: true });
      setAthleteId("");
      setApiKey("");
      setIntervalsSuccess(true);
      posthog.capture("onboarding:intervals_icu_connect_complete", { is_success: true });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to save credentials";
      posthog.capture("onboarding:intervals_icu_connect_complete", {
        is_success: false,
        error_code: errorMessage,
      });
      setIntervalsError(errorMessage);
    } finally {
      setIsIntervalsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setIsIntervalsLoading(true);
    setIntervalsError(null);

    try {
      await userService.deleteIntervalsICU();
      updateUser({ has_intervalsicu: false });
      setShowDisconnectDialog(false);
      setIntervalsSuccess(false);
    } catch (err) {
      setIntervalsError(
        err instanceof Error ? err.message : "Failed to disconnect"
      );
    } finally {
      setIsIntervalsLoading(false);
    }
  };

  const handleOpenTelegram = async () => {
    posthog.capture("onboarding:telegram_button_click");
    setIsTelegramLoading(true);
    setTelegramError(null);

    try {
      const result = await telegramService.generateLink();
      window.location.href = result.deep_link;
    } catch (err) {
      setTelegramError(
        err instanceof Error ? err.message : "Failed to open Telegram"
      );
    } finally {
      setIsTelegramLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-background">
      {/* Background layers with animation */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Animated gradient orbs */}
        <div
          className="absolute w-[500px] h-[500px] rounded-full blur-3xl opacity-15"
          style={{
            background: "oklch(0.75 0.15 55)",
            top: "10%",
            left: "20%",
            animation: "drift 25s ease-in-out infinite",
          }}
        />
        <div
          className="absolute w-[400px] h-[400px] rounded-full blur-3xl opacity-10"
          style={{
            background: "oklch(0.65 0.12 55)",
            bottom: "20%",
            right: "10%",
            animation: "drift 30s ease-in-out infinite reverse",
          }}
        />

        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(
                ellipse 80% 60% at 50% 30%,
                oklch(0.24 0.02 55 / 0.4) 0%,
                transparent 70%
              )
            `,
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `repeating-linear-gradient(
              -45deg,
              var(--foreground) 0,
              var(--foreground) 1px,
              transparent 1px,
              transparent 60px
            )`,
            maskImage: "radial-gradient(ellipse 70% 60% at 50% 40%, black 0%, transparent 70%)",
            WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 40%, black 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px]"
          style={{
            background: `
              radial-gradient(
                ellipse at center bottom,
                oklch(0.75 0.15 55 / 0.12) 0%,
                transparent 70%
              )
            `,
          }}
        />
      </div>

      {/* Keyframes for drift animation */}
      <style>{`
        @keyframes drift {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(30px, -20px) scale(1.05); }
          50% { transform: translate(-20px, 30px) scale(0.95); }
          75% { transform: translate(-30px, -10px) scale(1.02); }
        }
      `}</style>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header
          className="shrink-0 h-14 border-b border-border/50 px-6 flex items-center justify-between
                     animate-in fade-in slide-in-from-top-2 duration-500"
          style={{ animationFillMode: "backwards" }}
        >
          {/* User info */}
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full bg-primary/20 border border-primary/30
                         flex items-center justify-center text-sm font-medium text-primary"
            >
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <span className="text-sm text-foreground-muted hidden sm:block">
              {user?.name || "User"}
            </span>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogoutButtonClick}
            className="text-foreground-muted hover:text-destructive"
          >
            Logout
          </Button>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-auto">
          <div className="max-w-xl mx-auto px-6 py-10 space-y-12">
            {/* Welcome Section - Hero Style */}
            <section
              className="animate-in fade-in slide-in-from-bottom-4 duration-700"
              style={{ animationDelay: "100ms", animationFillMode: "backwards" }}
            >
              {/* Title */}
              <div className="text-center mb-10">
                <h1
                  className="text-4xl sm:text-5xl font-serif font-medium tracking-tight text-foreground mb-4"
                  style={{ fontFeatureSettings: '"ss01" 1', letterSpacing: "-0.02em" }}
                >
                  Hey{user?.name ? `, ${user.name.split(" ")[0]}` : ""}!
                </h1>
                <p
                  className="text-lg font-light tracking-wide max-w-md mx-auto"
                  style={{
                    background: `linear-gradient(135deg, oklch(0.75 0.15 55) 0%, oklch(0.7 0.18 40) 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Your AI running buddy who always knows.
                </p>
              </div>

              {/* Value proposition intro */}
              <div
                className="relative mb-10 p-6 rounded-2xl animate-in fade-in slide-in-from-bottom-2 duration-500"
                style={{
                  animationDelay: "200ms",
                  animationFillMode: "backwards",
                  background: `linear-gradient(
                    135deg,
                    oklch(0.18 0.02 55 / 0.6) 0%,
                    oklch(0.15 0.01 55 / 0.4) 100%
                  )`,
                  border: "1px solid oklch(0.75 0.15 55 / 0.15)",
                }}
              >
                {/* Decorative corner accents */}
                <div
                  className="absolute top-0 left-0 w-8 h-8 opacity-40"
                  style={{
                    borderLeft: "2px solid oklch(0.75 0.15 55)",
                    borderTop: "2px solid oklch(0.75 0.15 55)",
                    borderTopLeftRadius: "1rem",
                  }}
                />
                <div
                  className="absolute bottom-0 right-0 w-8 h-8 opacity-40"
                  style={{
                    borderRight: "2px solid oklch(0.75 0.15 55)",
                    borderBottom: "2px solid oklch(0.75 0.15 55)",
                    borderBottomRightRadius: "1rem",
                  }}
                />

                <p className="text-foreground-muted text-base leading-relaxed mb-5">
                  No more explaining your situation from scratch every time you
                  need advice. Connect your training data, and I'll already know
                  your{" "}
                  <span className="text-foreground font-medium">
                    weekly mileage
                  </span>
                  ,{" "}
                  <span className="text-foreground font-medium">
                    recent workouts
                  </span>
                  , and{" "}
                  <span className="text-foreground font-medium">
                    how that interval session went yesterday
                  </span>
                  .
                </p>

                <div className="space-y-3 mb-5">
                  <div className="flex items-start gap-3">
                    <span
                      className="flex-shrink-0 w-6 h-6 rounded-full text-xs flex items-center justify-center font-bold mt-0.5"
                      style={{
                        background: `linear-gradient(135deg, oklch(0.75 0.15 55) 0%, oklch(0.65 0.14 50) 100%)`,
                        color: "oklch(0.15 0.02 55)",
                      }}
                    >
                      1
                    </span>
                    <div>
                      <span className="text-foreground font-medium">
                        Connect Intervals.icu
                      </span>
                      <span className="text-foreground-muted">
                        {" "}
                        — I'll sync your training history so I can give advice
                        that actually fits your situation
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span
                      className="flex-shrink-0 w-6 h-6 rounded-full text-xs flex items-center justify-center font-bold mt-0.5"
                      style={{
                        background: `linear-gradient(135deg, oklch(0.75 0.15 55) 0%, oklch(0.65 0.14 50) 100%)`,
                        color: "oklch(0.15 0.02 55)",
                      }}
                    >
                      2
                    </span>
                    <div>
                      <span className="text-foreground font-medium">
                        Chat on Telegram
                      </span>
                      <span className="text-foreground-muted">
                        {" "}
                        — Ask me anything, anytime.{" "}
                        <span className="italic opacity-80">
                          "Should I run today?" "How do I pace my long run?" "My
                          knee feels weird — what now?"
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                <p
                  className="text-sm font-medium text-center pt-3"
                  style={{
                    borderTop: "1px solid oklch(0.75 0.15 55 / 0.1)",
                    color: "oklch(0.75 0.15 55)",
                  }}
                >
                  That's the whole setup. Two minutes, and you've got a running
                  buddy in your pocket.
                </p>
              </div>

            </section>

            {/* Intervals.icu Section */}
            <section
              className="animate-in fade-in slide-in-from-bottom-4 duration-700"
              style={{ animationDelay: "200ms", animationFillMode: "backwards" }}
            >
              <div className="relative group">
                <div
                  className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100
                             transition-opacity duration-500 blur-sm"
                  style={{
                    background: `linear-gradient(
                      135deg,
                      oklch(0.75 0.15 55 / 0.2) 0%,
                      transparent 50%,
                      oklch(0.75 0.15 55 / 0.1) 100%
                    )`,
                  }}
                />

                <div className="relative bg-card/80 backdrop-blur-sm border border-border rounded-2xl p-6 shadow-xl">
                  <div
                    className="absolute top-0 left-6 right-6 h-px"
                    style={{
                      background: `linear-gradient(
                        90deg,
                        transparent 0%,
                        var(--primary) 50%,
                        transparent 100%
                      )`,
                      opacity: 0.4,
                    }}
                  />

                  <div className="flex items-center gap-3 mb-4">
                    {/* Intervals.icu logo */}
                    <img
                      src={intervalsIcuLogo}
                      alt="Intervals.icu"
                      className="flex-shrink-0 w-10 h-10 rounded-lg"
                    />
                    <div>
                      <h2 className="text-lg font-medium text-foreground">
                        Connect Intervals.icu
                      </h2>
                      <p className="text-sm text-foreground-muted">
                        Link your training data for personalized advice
                      </p>
                    </div>
                  </div>

                  {isIntervalsConnected || intervalsSuccess ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                        <svg
                          viewBox="0 0 24 24"
                          className="w-5 h-5 text-emerald-400"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <span className="text-sm text-emerald-400">
                          Intervals.icu connected
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowDisconnectDialog(true)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        Disconnect account
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Why Intervals.icu - explanatory note */}
                      <div
                        className="mb-5 pl-3.5 border-l-2 py-0.5"
                        style={{ borderColor: "oklch(0.75 0.15 55 / 0.5)" }}
                      >
                        <p className="text-sm text-foreground-muted leading-relaxed">
                          During our alpha, we use{" "}
                          <span className="text-foreground font-medium">Intervals.icu</span>{" "}
                          as a free aggregator — connect your watches, trackers, and fitness
                          apps there, and Koach will access your training data to give
                          personalized advice.
                        </p>
                        <p className="text-xs text-foreground-subtle mt-1.5">
                          First, link your devices in Intervals.icu{" "}
                          <a
                            href="https://intervals.icu/settings/connections"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-foreground-muted hover:text-primary transition-colors"
                          >
                            (Settings → Connections)
                          </a>,
                          then connect it to Koach below.
                        </p>
                      </div>

                      {/* Step-by-step instructions */}
                      <p className="text-sm text-foreground-muted mb-3 font-medium">
                        How to get your credentials:
                      </p>

                      <ol className="space-y-2 mb-4">
                        {[
                          <>Go to{" "}
                            <a
                              href="https://intervals.icu/settings"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline underline-offset-2"
                            >
                              Settings
                            </a>
                          </>,
                          <>Scroll down to <span className="text-foreground font-medium">Developer Settings</span></>,
                          <>Copy your <span className="text-foreground font-medium">Athlete ID</span></>,
                          <>Click <span className="text-foreground font-medium">API Key</span> → <span className="text-foreground font-medium">Generate</span></>,
                          <>Copy the generated key — you're all set!</>,
                        ].map((step, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-foreground-muted">
                            <span
                              className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary
                                         text-xs font-medium flex items-center justify-center mt-0.5"
                            >
                              {i + 1}
                            </span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ol>

                      <div className="space-y-3">
                        <div className="space-y-1.5">
                          <label
                            htmlFor="athlete-id"
                            className="text-xs font-medium text-foreground-muted"
                          >
                            Athlete ID
                          </label>
                          <Input
                            id="athlete-id"
                            type="text"
                            placeholder="i12345"
                            value={athleteId}
                            onChange={(e) => setAthleteId(e.target.value)}
                            className="bg-background-elevated"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <label
                            htmlFor="api-key"
                            className="text-xs font-medium text-foreground-muted"
                          >
                            API Key
                          </label>
                          <div className="relative">
                            <Input
                              id="api-key"
                              type={showApiKey ? "text" : "password"}
                              placeholder="Your API key"
                              value={apiKey}
                              onChange={(e) => setApiKey(e.target.value)}
                              className="bg-background-elevated pr-16"
                            />
                            <button
                              type="button"
                              onClick={() => setShowApiKey(!showApiKey)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-foreground-muted
                                         hover:text-foreground transition-colors"
                            >
                              {showApiKey ? "Hide" : "Show"}
                            </button>
                          </div>
                        </div>
                      </div>

                      {intervalsError && (
                        <p className="mt-3 text-sm text-destructive">
                          {intervalsError}
                        </p>
                      )}

                      <Button
                        onClick={handleSaveIntervals}
                        disabled={
                          isIntervalsLoading ||
                          !athleteId.trim() ||
                          !apiKey.trim()
                        }
                        className="w-full mt-4"
                      >
                        {isIntervalsLoading ? (
                          <span className="flex items-center gap-2">
                            <svg
                              className="animate-spin h-4 w-4"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                              />
                            </svg>
                            Connecting...
                          </span>
                        ) : (
                          "Connect"
                        )}
                      </Button>

                      <p className="mt-3 text-xs text-foreground-subtle text-center">
                        This step is optional — you can always connect later.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </section>

            {/* Telegram Section - Primary CTA */}
            <section
              className="animate-in fade-in slide-in-from-bottom-4 duration-700"
              style={{ animationDelay: "300ms", animationFillMode: "backwards" }}
            >
              <div className="relative group">
                {/* Enhanced glow for primary CTA */}
                <div
                  className="absolute -inset-1 rounded-2xl opacity-60 group-hover:opacity-100
                             transition-opacity duration-500 blur-md"
                  style={{
                    background: `linear-gradient(
                      135deg,
                      oklch(0.75 0.15 55 / 0.3) 0%,
                      oklch(0.65 0.12 55 / 0.15) 50%,
                      oklch(0.75 0.15 55 / 0.2) 100%
                    )`,
                  }}
                />

                <div className="relative bg-card/90 backdrop-blur-sm border border-primary/30 rounded-2xl p-6 shadow-xl">
                  {/* Top accent line - brighter for CTA */}
                  <div
                    className="absolute top-0 left-6 right-6 h-px"
                    style={{
                      background: `linear-gradient(
                        90deg,
                        transparent 0%,
                        var(--primary) 50%,
                        transparent 100%
                      )`,
                      opacity: 0.6,
                    }}
                  />

                  <div className="flex items-start gap-3 mb-4">
                    {/* Telegram icon */}
                    <div
                      className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{
                        background: `linear-gradient(135deg, #229ED9 0%, #1E96C8 100%)`,
                      }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        className="w-5 h-5 text-white"
                        fill="currentColor"
                      >
                        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.44-.751-.245-1.349-.374-1.297-.789.027-.216.324-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.015 3.333-1.386 4.025-1.627 4.477-1.635.099-.002.321.023.465.141.121.1.154.234.17.331.015.098.034.32.018.495z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-lg font-medium text-foreground">
                        Chat on Telegram
                      </h2>
                      <p className="text-sm text-foreground-muted">
                        Ask me anything about your training
                      </p>
                    </div>
                  </div>

                  {telegramError && (
                    <p className="mb-3 text-sm text-destructive">
                      {telegramError}
                    </p>
                  )}

                  <button
                    onClick={handleOpenTelegram}
                    disabled={isTelegramLoading}
                    className="w-full h-12 rounded-xl font-medium text-white
                               flex items-center justify-center gap-2
                               transition-all duration-300 ease-out
                               hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/25
                               active:scale-[0.98]
                               disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    style={{
                      background: `linear-gradient(
                        135deg,
                        oklch(0.75 0.15 55) 0%,
                        oklch(0.68 0.14 50) 100%
                      )`,
                      boxShadow: `0 4px 14px 0 oklch(0.75 0.15 55 / 0.35)`,
                    }}
                  >
                    {isTelegramLoading ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Opening Telegram...
                      </>
                    ) : (
                      <>
                        <svg
                          viewBox="0 0 24 24"
                          className="w-5 h-5"
                          fill="currentColor"
                        >
                          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.44-.751-.245-1.349-.374-1.297-.789.027-.216.324-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.015 3.333-1.386 4.025-1.627 4.477-1.635.099-.002.321.023.465.141.121.1.154.234.17.331.015.098.034.32.018.495z" />
                        </svg>
                        Open Telegram
                        <svg
                          viewBox="0 0 24 24"
                          className="w-4 h-4 opacity-60"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M7 17L17 7" />
                          <path d="M7 7h10v10" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </section>
          </div>
        </main>

        {/* Footer */}
        <footer
          className="shrink-0 py-6 animate-in fade-in duration-700"
          style={{ animationDelay: "400ms", animationFillMode: "backwards" }}
        >
          {/* Decorative running track line */}
          <div className="max-w-xs mx-auto mb-4 relative">
            <div
              className="h-px w-full"
              style={{
                background: `linear-gradient(
                  90deg,
                  transparent 0%,
                  oklch(0.75 0.15 55 / 0.3) 20%,
                  oklch(0.75 0.15 55 / 0.5) 50%,
                  oklch(0.75 0.15 55 / 0.3) 80%,
                  transparent 100%
                )`,
              }}
            />
            {/* Animated runner dot */}
            <div
              className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full"
              style={{
                background: "oklch(0.75 0.15 55)",
                boxShadow: "0 0 8px oklch(0.75 0.15 55 / 0.6)",
                animation: "runnerDot 4s ease-in-out infinite",
              }}
            />
          </div>

          {/* Main footer content */}
          <div className="flex items-center justify-center gap-2 group">
            <span className="text-xs text-foreground-muted tracking-wide">
              Made with
            </span>

            {/* Animated heartbeat */}
            <span
              className="inline-flex items-center justify-center relative"
              style={{ animation: "heartbeat 1.2s ease-in-out infinite" }}
            >
              <span className="text-sm">❤️</span>
              {/* Pulse ring */}
              <span
                className="absolute inset-0 rounded-full"
                style={{
                  animation: "heartPulse 1.2s ease-out infinite",
                  background: "oklch(0.65 0.2 25 / 0.3)",
                }}
              />
            </span>

            <span className="text-xs text-foreground-muted tracking-wide">
              for
            </span>

            {/* "runners" with running figure animation on hover */}
            <span className="relative inline-flex items-center gap-1.5 text-xs group/runners">
              <span
                className="font-medium bg-clip-text text-transparent"
                style={{
                  backgroundImage: `linear-gradient(
                    135deg,
                    oklch(0.75 0.15 55) 0%,
                    oklch(0.7 0.18 40) 100%
                  )`,
                }}
              >
                runners
              </span>
              {/* Mini running figure that animates on hover */}
              <svg
                viewBox="0 0 16 16"
                className="w-3 h-3 opacity-0 group-hover/runners:opacity-100 transition-all duration-300
                           group-hover/runners:translate-x-0.5"
                style={{ color: "oklch(0.75 0.15 55)" }}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="10" cy="3" r="1.5" fill="currentColor" />
                <path d="M8 6c-1 1.5-2 2.5-3.5 3" />
                <path d="M7 7.5L5 12l-2 1" />
                <path d="M5 11l2.5-1.5 2 3" />
                <path d="M7.5 9.5l3-1 2 .5" />
              </svg>
            </span>
          </div>

          {/* Keyframes for footer animations */}
          <style>{`
            @keyframes heartbeat {
              0%, 100% { transform: scale(1); }
              15% { transform: scale(1.15); }
              30% { transform: scale(1); }
              45% { transform: scale(1.1); }
              60% { transform: scale(1); }
            }
            @keyframes heartPulse {
              0% { transform: scale(0.8); opacity: 0.6; }
              50% { transform: scale(1.8); opacity: 0; }
              100% { transform: scale(0.8); opacity: 0; }
            }
            @keyframes runnerDot {
              0%, 100% { left: 10%; opacity: 0; }
              10% { opacity: 1; }
              90% { opacity: 1; }
              100% { left: 90%; opacity: 0; }
            }
          `}</style>
        </footer>
      </div>

      {/* Logout confirmation dialog */}
      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent showCloseButton={false} className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Sign out?</DialogTitle>
            <DialogDescription>
              You'll need to sign in again to continue your session.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => setShowLogoutDialog(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleLogout}>
              Sign out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disconnect Intervals.icu confirmation dialog */}
      <Dialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <DialogContent showCloseButton={false} className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Disconnect Intervals.icu?</DialogTitle>
            <DialogDescription>
              This will remove your Intervals.icu credentials. Koach will no
              longer have access to your training data.
            </DialogDescription>
          </DialogHeader>
          {intervalsError && (
            <p className="text-sm text-destructive">{intervalsError}</p>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => setShowDisconnectDialog(false)}
              disabled={isIntervalsLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisconnect}
              disabled={isIntervalsLoading}
            >
              {isIntervalsLoading ? "Disconnecting..." : "Disconnect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
